import React from 'react'

import AuthProvider from 'attendee/Auth/Providers/AuthProvider'
import {OrganizationRoutes} from 'organization/OrganizationRoutes'
import OrganizationAuthProvider from 'organization/Auth/Providers/OrganizationAuthProvider'
import ThemeProvider from 'attendee/theme/ThemeProvider'
import OrganizationThemeProvider from 'organization/theme/OrganizationThemeProvider'
import {useIsInOrganizationRoutes} from 'utils/lib/url'
import OrganizationBillingProvider from 'organization/Billing/Providers/OrganizationBillingProvider'
import AttendeeBillingProvider from 'attendee/Billing/Providers/AttendeeBillingProvider'
import {AttendeeRoutes} from 'attendee/AttendeeRoutes'
import {GoogleOneTab} from 'attendee/Auth/GoogleOneTab'

export const routes = createRoutes({
  index: '/',
  loadingPage: '/loading-page',
  autologin: '/autologin',
  p: {
    ':affiliateToken': {},
  },
  search: '/search',
  info: '/info',
  help: '/help',
  prices: '/prices',
  organizations: '/organizations',
  affiliates: '/affiliates',
  cookies: '/cookies-policy',
  apiDocs: '/api-docs',
  terms: '/terms-and-conditions',
  privacy: '/privacy-policy',
  tickets: '/tickets',
  login: '/login',
  profile: '/profile',
  invoices: '/invoices',
  certificates: '/certificates',
  purchases: '/purchases',
  auth: {
    google: '/google',
  },
  register: '/register',
  verifyEmail: '/verify-email',
  passwordRecover: '/password-recover',
  billing: '/billing',
  organization: {
    autologin: '/autologin',
    login: '/login',
    register: '/register',
    verifyEmail: '/verify-email',
    passwordRecover: '/password-recover',
    profile: '/profile',
    billing: '/billing',
    auth: {
      google: '/google',
    },
    i18nStudio: '/i18n-studio',
    newEvent: '/new-event',
    list: '/list',
    ':organizationId': {
      events: '/events',
      invoices: '/invoices',
      earnings: '/earnings',
      help: '/help',
      settings: {
        connection: {
          ':stripeAccountId': {
            connected: '/connected',
          },
        },
      },
      affiliateProgram: '/affiliate-program',
      event: {
        create: {},
        ':eventId': {
          attendants: '/attendants',
          management: '/management',
          attendant: {
            ':attendantId': {
              view: '/view',
            },
          },
          page: '/page',
          settings: '/settings',
          tickets: '/tickets',
          earning: '/earning',
          integrations: {
            zapier: '/zapier',
            csvImport: '/csv-import',
          },
          addons: '/addons',
          products: '/products',
          cetrifications: '/cetrifications',
          publicPage: '/public-page',
          help: '/help',
        },
      },
    },
  },
  blog: {
    ':blogSlug': {},
  },
  ':eventSlug': {
    preview: '/preview',
  },
})

export function AllRoutes() {
  const isInOrganizationRoutes = useIsInOrganizationRoutes()

  if (isInOrganizationRoutes) {
    return (
      <OrganizationThemeProvider>
        <OrganizationAuthProvider>
          <OrganizationBillingProvider>
            <OrganizationRoutes />
          </OrganizationBillingProvider>
        </OrganizationAuthProvider>
      </OrganizationThemeProvider>
    )
  }

  return (
    <>
      <AuthProvider>
        <AttendeeBillingProvider>
          <ThemeProvider>
            <>
              <GoogleOneTab />
              <AttendeeRoutes />
            </>
          </ThemeProvider>
        </AttendeeBillingProvider>
      </AuthProvider>
    </>
  )
}

export type RoutesType = {
  [key: string]: string | RoutesType
}

type ExtendRecursively<O, T> = O extends object
  ? T & {[K in keyof O]: ExtendRecursively<O[K], T>}
  : O

function createRoutes<T extends RoutesType>(
  routes: T,
  namespace?: string,
): ExtendRecursively<T, {root: string}> {
  const childRoutes = Object.entries(routes).reduce((acc, [key, val]) => {
    if (typeof val === 'string') {
      const prependedVal = namespace ? `/${namespace}${val}` : val
      acc[key] = prependedVal
      return acc
    }

    const prependedKey = namespace ? `${namespace}/${key}` : key
    acc[key] = createRoutes(val, prependedKey)

    return acc
  }, {} as any)

  // append root
  childRoutes.root = namespace ? `/${namespace}` : '/'

  return childRoutes
}

/**
 * Turns a path from `routes` (always starts with `/`) into a React Router `path` string.
 * Locale lives only in the real URL; `<Routes location={useRoutesLocationUnderLocale()}>` matches these patterns.
 */
export function pathForRoute(href: string): string {
  const p = href.startsWith('/') ? href.slice(1) : href
  if (!p) {
    throw new Error(
      'Use `<Route index />` for home; pathForRoute is not valid for "/".',
    )
  }
  return p
}

export function routesWithValue<T>(param: string, value: string, routes: T): T {
  // @ts-ignore
  return Object.entries(routes).reduce((acc, [key, route]) => {
    if (typeof route === 'string') {
      const withParam = route.replace(param, value)
      acc[key] = withParam
      return acc
    }

    acc[key] = routesWithValue(param, value, route)
    return acc
  }, {} as any)
}
